import { useMemo } from 'react';
import { quoteBasePrice } from '~/lib/quotes';
import {
  AvailableExtra,
  BasketItem,
  BasketQuote,
  QuoteExtras,
  QuoteExtrasCalculations
} from '~/lib/types/basket-types';
import { BasketSections } from '~/lib/types/enums';
import { Protection } from '~/lib/types/extra-types';
import { onlyUniqueNonBlank, SumNumber } from '~/lib/utils/array';
import { ToSurchargeType } from '~/lib/utils/breakdown';

type PriceBreakdown = {
  tax: number;
  subTotal: number;
  total: number;
  quote: BasketQuote;
  upsells: AvailableExtra[];
  basePrice: number;
  nonBasePriceItems: { amount: number; name: string }[];
  localTaxes: number;
  protection: number;
  localTax: number;
};

export const GetCurrency = (item: BasketItem) =>
  item && (item?.sections[BasketSections.QUOTE].payload as BasketQuote)?.currency;

export const GenerateBreakDown = (item: BasketItem, takenExtras?: AvailableExtra[]): PriceBreakdown => {
  const extrasPayload = item.sections[BasketSections.EXTRAS]?.payload as QuoteExtras | undefined;
  const quotePayload = item.sections[BasketSections.QUOTE].payload as BasketQuote;
  const calculations = item.sections[BasketSections.EXTRAS_CALCULATIONS]?.payload as QuoteExtrasCalculations;

  const selectedExtras =
    takenExtras ||
    (extrasPayload?.extras
      .map((extra) => quotePayload.availableExtras.find((qExtra) => qExtra.key === extra.key))
      .filter((x) => x !== undefined && x !== null) as AvailableExtra[]) ||
    [];

  const localTaxes = calculations?.ddp?.ddpFee || 0;
  const ddpAdminFee = calculations?.ddp?.ddpAdminFee || 0;

  // This will combine 'Length Out of Gauge' and 'Weight Out of Gauge' into OOG to show in the breakdown
  // The name will get used to generate the translation key in the QuotePrice component
  // The ToSurchargeDefaultName function will be used to get the default translation,
  // eg OOG: `ORDER_BREAKDOWN_${x.name.toUpperCase()}_PRICE` -> ORDER_BREAKDOWN_OOG_PRICE -> Out Of Gauge Charge
  const nonBasePrice =
    quotePayload.price.breakdown
      ?.map((x) => ToSurchargeType(x.metadata?.surcharge))
      .filter(onlyUniqueNonBlank)
      .map((x) => ({
        name: x || '',
        amount: SumNumber(
          quotePayload.price.breakdown.filter((e) => ToSurchargeType(e.metadata?.surcharge) === x).map((e) => e.amount)
        )
      })) || [];

  if (ddpAdminFee > 0) {
    nonBasePrice.push({ name: 'DDP Admin Fee', amount: ddpAdminFee });
  }

  const tax = quotePayload?.price.tax + SumNumber(selectedExtras.map((extra) => extra.price.tax));
  const subTotal = quotePayload?.price.net + SumNumber(selectedExtras.map((extra) => extra.price.net)) + ddpAdminFee;
  const basePrice = quoteBasePrice(quotePayload);
  const localTax = quotePayload?.price?.breakdown
    ?.filter((x) => x.metadata?.surcharge === 'local-tax')
    ?.map((x) => x.amount)
    ?.reduce((a, b) => a + b, 0);

  return {
    tax,
    subTotal: subTotal,
    total: subTotal + tax + localTaxes + localTax,
    quote: quotePayload,
    upsells: selectedExtras,
    basePrice: basePrice,
    nonBasePriceItems: nonBasePrice,
    localTaxes: localTaxes,
    localTax: localTax,
    protection: SumNumber(
      selectedExtras.filter((x) => x.key === Protection.BASE || x.key === Protection.FULL).map((x) => x.price?.net || 0)
    )
  };
};

export function useBasketItemPrice(item: BasketItem, extras?: AvailableExtra[]) {
  const total = useMemo(() => GenerateBreakDown(item, extras), [item, extras]);
  return total;
}
